import { getRouteApi } from '@tanstack/react-router'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import DataBlock from '@/components/DataBlock/DataBlock'
import LinkText from '@/components/LinkText/LinkText'
import Table, { type Column } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import ToggleFavourite from '@/components/common/ToggleFavourite/ToggleFavourite'
import { useNumberColumn } from '@/hooks/useColumns'
import { useScreenResolution } from '@/hooks/useScreenResolution'

import styles from './ClassDetailsView.module.scss'
import useClass, { type Student } from '../queries/useClass'

const routeApi = getRouteApi(
  '/_auth/students-and-classes/classes/$classId/details'
)

const ClassDetailsView = () => {
  const { t } = useTranslation('classes')
  const { classId } = routeApi.useParams()

  const { data: classData, isLoading } = useClass(classId)

  const { isMobile } = useScreenResolution()

  const basicInformationInfo = [
    {
      label: t('label.academic-level'),
      value: classData?.academicLevel?.name,
      dataTestId: 'academic-level'
    },
    {
      label: t('label.capacity'),
      value: (
        <span>
          <span>{classData?.numberOfStudents}</span>/
          <span
            className={clsx(
              typeof classData?.capacity !== 'number' && styles.noCapacity
            )}
          >
            {classData?.capacity ?? '-'}
          </span>
        </span>
      ),

      dataTestId: 'class-capacity'
    },
    {
      label: t('label.preferred-room'),
      value: classData?.preferredRoom?.name,
      dataTestId: 'preffered-room'
    }
  ]
  const tutorInfo = [
    {
      label: t('label.name'),
      value: classData?.tutor?.fullName,
      dataTestId: 'tutor'
    },
    {
      label: t('label.phone-number'),
      value: classData?.tutor?.phoneNumber,
      dataTestId: 'tutor-phone-number'
    },
    {
      label: t('label.email'),
      value: classData?.tutor?.email,
      dataTestId: 'tutor-email'
    }
  ]

  const tableColumns: Column<Student>[] = [
    useNumberColumn(),
    {
      key: 'student',
      title: t('header.student-name'),
      width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
      render: (value: Student) => (
        <LinkText
          dataTestId="student"
          to="/students-and-classes/students/$studentId/details"
          params={{
            studentId: value.id
          }}
        >
          {value.fullName}
        </LinkText>
      )
    },
    {
      dataIndex: 'excusedLessonsCount',
      key: 'excusedLessonsCount',
      title: t('header.exc-absences'),
      width: TABLE_CELL_WIDTH.MD
    },
    {
      dataIndex: 'unexcusedLessonsCount',
      key: 'unexcusedLessonsCount',
      title: t('header.unexc-absences'),
      width: TABLE_CELL_WIDTH.MD
    },
    {
      dataIndex: 'latenessLessonsCount',
      key: 'latenessLessonsCount',
      title: t('header.lateness'),
      width: TABLE_CELL_WIDTH.MD
    },
    {
      dataIndex: 'commentsCount',
      key: 'commentsCount',
      title: t('header.comments'),
      width: TABLE_CELL_WIDTH.MD
    },
    {
      dataIndex: 'latenessLessonsCount',
      key: 'latenessLessonsCount',
      title: t('header.lessons-per-week'),
      width: TABLE_CELL_WIDTH.MD
    }
  ]

  return (
    <>
      <div className={styles.sectionWrapper}>
        <div className={styles.headerWrapper}>
          <h2 className={styles.sectionHeader}>{t('header.details')}</h2>
          <ToggleFavourite />
        </div>
        <div className={styles.dataBlocksWrapper}>
          <DataBlock
            header={t('header.basic-information')}
            infoArray={basicInformationInfo}
          />
          <DataBlock header={t('header.tutor')} infoArray={tutorInfo} />
        </div>
      </div>

      <div className={styles.sectionWrapper} data-test-id="students-section">
        <h2 className={styles.sectionHeader}>{t('header.students')}</h2>

        <Table
          id="students-table"
          data={classData?.students || []}
          isLoading={isLoading}
          columns={tableColumns}
          showTopBar={false}
          caption={t('header.students')}
        />
      </div>
    </>
  )
}

export default ClassDetailsView
